import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { URLService } from './dynamic-url.service';

/**
 * A saved AI "student" persona for Practice Roleplay. Institution-scoped on the backend —
 * the caller only ever sees personas for their own institution. `agentId` is the backing
 * AgentConfig the roleplay worker fetches. (Named `PracticeRoleplay*` to avoid clashing
 * with the unrelated ACL `Personas` in acl.service.ts.)
 */
export interface RoleplayPersona {
  personaId: string;
  name: string;
  prompt: string;
  agentId: string;
  /** The persona's real backing Pencil user (isAgent). Absent on personas created before
   * agent identities existed — the API omits the field rather than sending "undefined". */
  agentUserId?: string;
}

/** A saved end-of-session report — the structured review the worker delivered via
 * `end_session`, persisted against the persona + the tutor who ran the session. */
export interface RoleplayReport {
  reportId: string;
  personaId: string;
  userId: string;
  userName: string;
  score: number | null;
  headline: string;
  summary: string;
  strengths: string[];
  improvements: string[];
  keyMoment: string;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class PracticeRoleplayService {
  /**
   * Fires when the app-shell "New persona" header button (beside the avatar, mirroring the
   * People page's Invite button) is clicked. `PracticeRoleplayComponent` subscribes and opens
   * the create dialog — the button lives in the shell, the dialog in the feature component.
   */
  readonly newPersona = new Subject<void>();

  constructor(private readonly http: HttpClient, private readonly urls: URLService) {}

  /** `${apiServerUrl}/personas`, honoring a `?serverurl=` override (URLService). */
  private base(): string {
    return `${this.urls.getDynamicUrl()}/personas`;
  }

  /** All personas for the current user's institution, newest first. */
  list(): Observable<RoleplayPersona[]> {
    return this.http
      .get<{ personas: RoleplayPersona[] }>(this.base())
      .pipe(map((r) => r?.personas ?? []));
  }

  /** Create a persona (name + character prompt). Mints the backing AgentConfig server-side. */
  create(name: string, prompt: string): Observable<RoleplayPersona> {
    return this.http.post<RoleplayPersona>(this.base(), { name, prompt });
  }

  /** Delete a persona (and its backing AgentConfig). Institution-guarded server-side. */
  delete(personaId: string): Observable<unknown> {
    return this.http.delete(`${this.base()}/${encodeURIComponent(personaId)}`);
  }

  /** Save an end-of-session report, attributed to the current tutor. Keyed by `agentId` —
   * the space route only ever carries `?agentid=` (the persona's backing AgentConfig id),
   * which is all the FE has at session-end time; the server resolves the owning persona. */
  saveReport(
    agentId: string,
    report: Pick<
      RoleplayReport,
      'score' | 'headline' | 'summary' | 'strengths' | 'improvements' | 'keyMoment'
    > & { spaceId?: string },
  ): Observable<RoleplayReport> {
    return this.http.post<RoleplayReport>(`${this.base()}/reports`, { agentId, ...report });
  }

  /** Past reports for a persona, newest first — every tutor in the institution who has
   * practiced with it. */
  listReports(personaId: string): Observable<RoleplayReport[]> {
    return this.http
      .get<{ reports: RoleplayReport[] }>(
        `${this.base()}/${encodeURIComponent(personaId)}/reports`,
      )
      .pipe(map((r) => r?.reports ?? []));
  }
}
